import { Fragment, ReactNode } from "react";

/**
 * A deliberately tiny markdown renderer for assistant replies: paragraphs, line breaks, bullet and
 * numbered lists, `**bold**` and `# headings` (shown bold). It only ever builds React elements
 * from text — never HTML — so model output cannot inject markup.
 */

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul" | "ol"; items: string[] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;

export const parseBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  const last = () => blocks[blocks.length - 1];

  text.split(/\r?\n/).forEach((raw) => {
    const bullet = raw.match(BULLET);
    const numbered = raw.match(NUMBERED);
    const listKind = bullet ? "ul" : numbered ? "ol" : null;

    if (listKind) {
      const item = (bullet ?? numbered)![1];
      const current = last();
      if (current && current.kind === listKind) current.items.push(item);
      else blocks.push({ kind: listKind, items: [item] });
      return;
    }
    if (raw.trim() === "") {
      blocks.push({ kind: "p", lines: [] });
      return;
    }
    const line = raw.match(HEADING) ? `**${raw.match(HEADING)![1]}**` : raw;
    const current = last();
    if (current && current.kind === "p") current.lines.push(line);
    else blocks.push({ kind: "p", lines: [line] });
  });

  return blocks.filter((b) => (b.kind === "p" ? b.lines.length > 0 : true));
};

/** `**bold**` → <strong>; everything else stays plain text. */
export const renderInline = (text: string): ReactNode[] =>
  text
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part, i) =>
      part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      )
    );

const SimpleMarkdown = ({ text }: { text: string }) => (
  <div className="offer-assistant__markdown">
    {parseBlocks(text).map((block, bi) => {
      if (block.kind === "p") {
        return (
          <p key={bi}>
            {block.lines.map((line, li) => (
              <Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(line)}
              </Fragment>
            ))}
          </p>
        );
      }
      const List = block.kind;
      return (
        <List key={bi}>
          {block.items.map((item, ii) => (
            <li key={ii}>{renderInline(item)}</li>
          ))}
        </List>
      );
    })}
  </div>
);

export default SimpleMarkdown;
